import { PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid } from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { CHART_CONFIG } from "../../utils/constants"
import type { VarValueBreakdownItem, PinnedVarChartType } from "../../hooks/useVariablesAnalytics"

// Chart for one variable's value breakdown. Shared by the Overview pins and the Forms tab
export function VarBreakdownChart({
  breakdown,
  chartType = "bar",
  labelFor = (v: string) => v,
}: {
  breakdown: VarValueBreakdownItem[]
  chartType?: PinnedVarChartType
  labelFor?: (value: string) => string
}) {
  if (breakdown.length === 0) {
    return <p className="text-xs text-muted-foreground text-center py-4">No data</p>
  }

  // `label` is what's shown (option labels for form fields); `value` stays the raw stored value
  const withLabels = breakdown.map(b => ({ ...b, label: labelFor(b.value) }))
  const chartData = withLabels.slice(0, chartType === "pie" ? 8 : 10)
  const total = breakdown.reduce((s, b) => s + b.count, 0)

  if (chartType === "number") {
    return (
      <div className="space-y-1 max-h-[200px] overflow-y-auto">
        {withLabels.slice(0, 10).map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ background: item.color }} />
            <span className="text-xs truncate flex-1" title={item.label}>{item.label}</span>
            <span className="text-xs font-semibold tabular-nums">{item.count}</span>
            <span className="text-[10px] text-muted-foreground w-8 text-right">{total > 0 ? Math.round(item.count / total * 100) : 0}%</span>
          </div>
        ))}
      </div>
    )
  }

  if (chartType === "pie") {
    return (
      <ChartContainer config={CHART_CONFIG} className="h-[200px] w-full min-w-0 aspect-auto">
        <PieChart>
          <Pie data={chartData as any[]} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={70} label={(props: any) => `${String(props.label ?? '').slice(0, 10)}${String(props.label ?? '').length > 10 ? '…' : ''} ${(((props.percent as number) ?? 0) * 100).toFixed(0)}%`} labelLine={false}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Pie>
          <ChartTooltip content={<ChartTooltipContent />} />
        </PieChart>
      </ChartContainer>
    )
  }

  return (
    <ChartContainer config={CHART_CONFIG} className="w-full min-w-0 aspect-auto" style={{ height: Math.max(100, Math.min(chartData.length, 8) * 28 + 20) }}>
      <BarChart data={chartData} layout="vertical" barCategoryGap={4}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
        <XAxis type="number" className="text-xs" tick={{ fontSize: 10 }} />
        <YAxis dataKey="label" type="category" width={90} className="text-xs" tick={{ fontSize: 10 }} tickFormatter={v => String(v).length > 11 ? String(v).slice(0, 11) + '…' : v} />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Bar dataKey="count" name="Count" barSize={14}>
          {chartData.map((entry, i) => (
            <Cell key={i} fill={entry.color} />
          ))}
        </Bar>
      </BarChart>
    </ChartContainer>
  )
}
